<script setup>
import ModalBox from '@/components/modals/ModalBox.vue'
import { useLegacyModalBridge } from '@/composables/useLegacyModalBridge'
import { computed, ref, onMounted, onBeforeUnmount, watch, nextTick, getCurrentInstance } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import _ from "lodash"
import Spinner from "@/components/ui/Spinner.vue"
import StreamService from "@/services/StreamService"
import AlertModal from "@/components/modals/AlertModal.vue"
import UploadAlertModal from "@/components/modals/UploadAlertModal.vue"
import UppyUploaderReplace from "../../components/UppyUploaderReplace.vue"
import FormButton from "../../components/Atoms/FormButton.vue"
import VODBreadcrumb from "../../components/VOD/VODBreadcrumb.vue"
import VODVideoDetailsForm from "../../components/VOD/VODVideoDetailsForm.vue"
import IconBase from "../../components/icon/IconBase.vue"
import BackButton from '@/components/Atoms/BackButton.vue'
import IconChevronLeft from "../../components/icon/IconChevronLeft.vue"
import VODEmbedTab from "../../components/VOD/VODEmbedTab.vue"
import VODChaptersTab from "../../components/VOD/VODChaptersTab.vue"
import VODCaptionsTab from "../../components/VOD/VODCaptionsTab.vue"
import VODScheduleTab from "../../components/VOD/VODScheduleTab.vue"
import VODMetaTab from "../../components/VOD/VODMetaTab.vue"
import VODSocialTab from "../../components/VOD/VODSocialTab.vue"
import VODClipsTab from "../../components/VOD/VODClipsTab.vue"
import TabGroup from "../../components/Atoms/TabGroup.vue"
import TabItem from "../../components/Atoms/TabItem.vue"
import VODAnalytics from './VODAnalytics.vue'
import Tooltip from '../../components/Atoms/Tooltip.vue'
import Title from "../../components/ui/Title.vue"
import VODTags from "../../components/ui/VODTags.vue"
import { applyTabToQuery, parseTabQuery } from '@/Utils/vodVideoManageQuery'

const { isVisible, hide: hideLegacyModal } = useLegacyModalBridge('cloud-import-replace')
const { notify } = useNotification()
const store = useStore()
const instance = getCurrentInstance()
const root = instance.proxy.$root
const route = useRoute()
const router = useRouter()

const props = defineProps({
  stream: {
    type: Object,
    required: true,
  },
})

const activeTab = ref("embed")
const playbackUrl = ref('')
const streamMeta = ref(null)

const maxPosterUploadMB = ref(2)
const videoMeta = ref(null)

const clips = ref([])

const availableEncodingPresets = ref([
  { id: "360p", text: "360p", minHeight: 300 },
  { id: "480p", text: "480p", minHeight: 440 },
  { id: "720p", text: "720p", minHeight: 700 },
  { id: "1080p", text: "1080p", minHeight: 1020 },
])
const maxTotalFileSize = ref(0)
const aioStorageLimit = ref({})
const storageRestricitonsLoaded = ref(false)

const scopeAlive = ref(true)
const videoResolving = ref(false)

const vodFiles = computed(() => store.getters['Streams/vodFiles'])
const routeVideoKey = computed(() => `${route.params.streamId}/${route.params.videoId}`)

const maxVideoFileSize = computed(() => {
  let maxSize = null

  if (props.stream.type === "vod" && aioStorageLimit.value) {
    maxSize = 1000
    return maxSize
  }
  return maxSize
})
const canEncodeVideos = computed(() => {
  const encodingUnits = _.get(
    store.state.User.baseSub,
    "package.definition.encodingUnits"
  )
  return encodingUnits > 0
})

const maxFileSizeErrorMessage = computed(() => {
  const assignedSpace = store.state.User.maxVodStorage
  return `Your VOD storage limit (${assignedSpace}GB) has been exceeded. Upgrade now to increase your video upload capacity.`
})

const isLiveToVod = computed(() => {
  return props.stream.liveParent
})
const isTrialOnly = computed(() => {
  const subs = store.state.User.subscriptions
  const active = subs.filter(
    (x) => x.enabled && ["vod", "live"].includes(x.category)
  )
  const allTrial = active.filter(
    (x) => x.package.name && /trial/gi.test(x.package.name)
  )
  return !active.length || allTrial.length === active.length
})
const currentFile = computed(() => {
  return vodFiles.value?.find((file) => file.id === route.params.videoId)
})
const showVideoSpinner = computed(() => videoResolving.value && !currentFile.value)

// Header shows the name without the file extension (raw name in tooltip)
const displayFileName = computed(() => (currentFile.value?.fileName || '').replace(/\.[a-z0-9]{2,4}$/i, ''));

// Sub-folder (visual grouping) the video belongs to, for the breadcrumb
const videoSubfolder = computed(() => {
  const id = currentFile.value?.subfolder
  if (!id) return null
  return (store.getters['Streams/vodSubfolders'] || []).find((s) => s._id === id) || null
})
const backTo = computed(() => {
  if (videoSubfolder.value) {
    return `/vods/${props.stream?._id}/videos?subfolder=${videoSubfolder.value._id}`
  }
  return `/vods/${props.stream?._id}`
})
const breadcrumbItems = computed(() => {
  const items = [
    { label: 'Content Library', to: '/vods' },
    { label: props.stream?.name, to: `/vods/${props.stream?._id}` },
  ]
  if (videoSubfolder.value) {
    items.push({
      label: videoSubfolder.value.name,
      to: `/vods/${props.stream?._id}/videos?subfolder=${videoSubfolder.value._id}`,
    })
  }
  items.push({ label: displayFileName.value })
  return items
})
const fileExt = computed(() => {
  const m = (currentFile.value?.fileName || '').match(/\.[a-z0-9]{2,4}$/i)
  return m ? m[0] : ''
})
// Rename from the header title. Keeps the original file extension so a display
// edit of "clip" doesn't silently drop ".mp4".
const onRenameTitle = async (newName) => {
  const base = (newName || '').trim().replace(/\.[a-z0-9]{2,4}$/i, '')
  if (!base) return
  const finalName = base + fileExt.value
  if (finalName === currentFile.value?.fileName) return
  try {
    if (props.stream?.liveParent) {
      await StreamService.setStreamDvrArchiveName({
        streamId: props.stream.liveParent,
        from: currentFile.value.archive.from,
        duration: currentFile.value.archive.duration,
        name: finalName,
      })
    } else {
      await StreamService.updateStreamPlaylistVideoFilename(props.stream._id, currentFile.value.id, finalName)
    }
    await store.dispatch("Streams/getStreams")
    await store.dispatch("Streams/getFilesInVODFolder", { streamId: props.stream._id })
    notify({ group: "success", text: "Title updated" })
  } catch (e) {
    notify({ group: "error", text: "Could not rename video" })
  }
}

const playerUrl = computed(() => {
  if (!currentFile.value) return ''
  if (currentFile.value?.dvr) {
    const range = `${currentFile.value?.from}-${currentFile.value?.duration}`
    return `${process.env.VUE_APP_PLAYER_APP_BASE_URL}/vod/${currentFile.value.streamKey
      }?range=${range}&preview=true&abr=${currentFile.value.abr ? "true" : "false"
      }&server=${currentFile.value.server}`
  }

  if (currentFile.value?.archive) {
    const range = `${currentFile.value?.archive?.from}-${currentFile.value?.archive?.duration}`
    return (
      `${process.env.VUE_APP_PLAYER_APP_BASE_URL}/${currentFile.value.archive.key
      }?range=${range}&preview=true&abr=${currentFile.value?.archive?.abr ? "true" : "false"
      }`
      + (currentFile.value?.archive?.namedHls ? "&namedHls=true" : "")
      + (currentFile.value?.archive?.cap ? "&cap=true" : "")
    )
  }
  const playerUri = `${process.env.VUE_APP_PLAYER_APP_BASE_URL
    }/vod/${currentFile.value?.id?.replace(".mp4", "")}?preview=true`
  return playerUri
})

const enrichVideoDetail = async (streamId, videoId) => {
  if (!currentFile.value?._id) return
  const fileBackendId = currentFile.value._id
  try {
    const videoMetaData = await StreamService.getStreamPlaylistVideoMetaData(videoId)
    if (!scopeAlive.value || routeVideoKey.value !== `${streamId}/${videoId}`) return
    const streamMetadata = await StreamService.getStreamMetadata(streamId, fileBackendId)
    if (!scopeAlive.value || routeVideoKey.value !== `${streamId}/${videoId}`) return
    videoMeta.value = streamMetadata?.videoMeta ?? videoMetaData
    streamMeta.value = streamMetadata
    clips.value = await StreamService.getVodClips(streamId, fileBackendId)
  } catch (e) {
    console.error(e)
  }
}

const ensureVideoFile = async () => {
  const streamId = route.params.streamId
  const videoId = route.params.videoId
  if (!streamId || !videoId) return

  if (currentFile.value) {
    enrichVideoDetail(streamId, videoId)
    return
  }

  videoResolving.value = true
  try {
    const folderId = store.getters['Streams/vodFilesFolderId']
    if (folderId !== streamId) {
      store.dispatch('Streams/prepareVodFolder', streamId)
    }
    await store.dispatch('Streams/getFilesInVODFolder', { streamId })
    if (!scopeAlive.value || route.params.streamId !== streamId) return
    if (!currentFile.value) {
      notify({ group: 'error', text: 'Video not found' })
      await router.push({ name: 'VODBucketVideos', params: { streamId } })
      return
    }
    await enrichVideoDetail(streamId, videoId)
  } finally {
    if (route.params.streamId === streamId) videoResolving.value = false
  }
}

onMounted(() => {
  store.dispatch("Ui/getAllTags")
  if (props.stream?._id && !props.stream?.liveParent) {
    store.dispatch("Streams/getVodSubfolders", props.stream._id)
  }
  ensureVideoFile()
})

watch(routeVideoKey, () => {
  syncActiveTabFromRoute()
  store.dispatch(
    "Streams/getVodSubfolders",
    props.stream?.liveParent ? null : route.params?.streamId,
  )
  ensureVideoFile()
})

const getData = async () => {
  await enrichVideoDetail(route.params.streamId, route.params.videoId)
}

const isTabActive = (tab) => {
  return tab === activeTab.value
}

// Segmented control tabs for the video detail right column (design 8459-45918).
const videoTabs = computed(() => {
  const list = [
    { key: 'embed', label: 'Embed' },
    { key: 'player', label: 'Player' },
    { key: 'chapters', label: 'Chapters' },
  ]
  list.push({ key: 'captions', label: 'Captions', disabled: isTrialOnly.value, hint: isTrialOnly.value ? 'Upgrade to use this feature.' : '' })
  if (!isLiveToVod.value) {
    list.push({ key: 'clips', label: 'Clips' })
  }
  list.push({ key: 'schedule', label: 'Schedule' })
  list.push({ key: 'analytics', label: 'Analytics' })
  return list
})

const resolveTabKey = (key) => {
  const tab = videoTabs.value.find((t) => t.key === key)
  if (tab && !tab.disabled) return key
  return 'embed'
}

const syncActiveTabFromRoute = () => {
  activeTab.value = resolveTabKey(parseTabQuery(route.query.tab))
}

const setTab = (tab) => {
  const key = resolveTabKey(tab)
  activeTab.value = key
  router.replace({ query: applyTabToQuery(route.query, key) })
}

watch(
  () => route.query.tab,
  () => {
    syncActiveTabFromRoute()
  },
)

watch(videoTabs, () => {
  const resolved = resolveTabKey(activeTab.value)
  if (resolved !== activeTab.value) {
    setTab(resolved)
  }
})

// --- Scrollable tab bar: arrows + drag-to-scroll (same UX as the livestream dashboard) ---
const tabBarRef = ref(null)
const tabCanScrollLeft = ref(false)
const tabCanScrollRight = ref(false)
const updateTabScroll = () => {
  const el = tabBarRef.value
  if (!el) return
  tabCanScrollLeft.value = el.scrollLeft > 1
  tabCanScrollRight.value = Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth - 1
}
const scrollTabs = (dir) => {
  const el = tabBarRef.value
  if (!el) return
  el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.6, 160), behavior: 'smooth' })
}

// Click-and-drag to scroll (desktop mouse); touch keeps native momentum scroll.
let tabDragActive = false
let tabDragCapturing = false
let tabDragStartX = 0
let tabDragStartScroll = 0
const tabDragMoved = ref(false)
const onTabPointerDown = (e) => {
  const el = tabBarRef.value
  if (!el || e.pointerType !== 'mouse' || e.button !== 0) return
  tabDragActive = true
  tabDragCapturing = false
  tabDragMoved.value = false
  tabDragStartX = e.clientX
  tabDragStartScroll = el.scrollLeft
}
const onTabPointerMove = (e) => {
  if (!tabDragActive) return
  const el = tabBarRef.value
  if (!el) return
  const dx = e.clientX - tabDragStartX
  if (!tabDragCapturing) {
    if (Math.abs(dx) <= 3) return
    tabDragCapturing = true
    tabDragMoved.value = true
    el.setPointerCapture?.(e.pointerId)
    el.classList.add('tab-dragging')
  }
  el.scrollLeft = tabDragStartScroll - dx
}
const onTabPointerUp = (e) => {
  if (!tabDragActive) return
  tabDragActive = false
  const el = tabBarRef.value
  if (el && tabDragCapturing) {
    el.releasePointerCapture?.(e.pointerId)
    el.classList.remove('tab-dragging')
  }
  tabDragCapturing = false
  // Keep tabDragMoved truthy through the click that follows a drag-release so the
  // click handler can suppress an accidental tab switch, then reset next tick.
  setTimeout(() => { tabDragMoved.value = false }, 0)
}
// Suppress the click right after a drag so dragging never switches tabs.
const onTabClickCapture = (e) => {
  if (tabDragMoved.value) {
    e.preventDefault()
    e.stopPropagation()
  }
}
// Re-check overflow when the tab set changes (e.g. Live-to-VOD hides tabs).
watch(videoTabs, () => nextTick(updateTabScroll))
onMounted(() => {
  syncActiveTabFromRoute()
  updateTabScroll()
  setTimeout(updateTabScroll, 300)
  window.addEventListener('resize', updateTabScroll)
})
onBeforeUnmount(() => {
  scopeAlive.value = false
  window.removeEventListener('resize', updateTabScroll)
})

const onFinishedUploadingVideoThroughCloud = async (video) => {
  const streamId = props.stream._id
  const streamKey = props.stream.key
  const newVideoId = video.videoId || video.id
  video.file = {
    name: currentFile.value.fileName,
    size: video.bytes,
  }
  video.precedence = currentFile.value.precedence
  await StreamService.onCompleteVodReplacement(
    currentFile.value.id,
    newVideoId,
    streamId,
    streamKey,
    video
  )
  // this will clear captions, chapeters, schedule, and posters.
  await StreamService.clearVodMetadata(
    streamId,
    currentFile.value.id,
    currentFile.value._id
  )
  notify({
    group: "success",
    text: "Video replaced successfully.",
  })

  // Refresh data without page reload
  await store.dispatch("Streams/getStreams")
  await store.dispatch("Streams/getFilesInVODFolder")
  await getData()
}

const onClickAnalytics = () => {
  const newRoute = router.resolve({
    name: "AnalyticsDashboardWithRoute",
    params: { videoId: currentFile?.value.refId ?? route.params.videoId },
    query: { folderId: props.stream._id },
  })
  window.open(newRoute.href, "_blank")
}

const videoMetadataScheduleUpdated = (meta) => {
  if (!videoMeta?.value?.schedule) {
    videoMeta.value = { ...videoMeta.value, schedule: meta }
  }
  videoMeta.value.schedule = meta
}

const videoMetadataCaptionsUpdated = (captions) => {
  if (!videoMeta?.value?.captions) {
    videoMeta.value = { ...videoMeta.value, captions: captions }
  }
  videoMeta.value.captions = captions
}

const videoMetadataAddonCaptionsUpdated = (captions) => {
  if (!videoMeta?.value?.addonCaptions) {
    videoMeta.value = { ...videoMeta.value, addonCaptions: captions }
  }
  videoMeta.value.addonCaptions = captions
}

const getPlayerUrl = () => {
  if (currentFile.value) {
    let playerUri = currentFile.value.embedUrl
    if (!playerUri) {
      let vid = currentFile.value?.id
      if (vid.split(".")[1] === "mp4") {
        vid = vid.replace(".mp4", "")
      }
      playerUri = `${process.env.VUE_APP_PLAYER_APP_BASE_URL}/vod/${vid}`
    }
    playerUri = playerUri.replace(
      "https://player.castr.io",
      process.env.VUE_APP_PLAYER_APP_BASE_URL
    )
    playerUri = playerUri
    return playerUri
  }
}
const getClips = async () => {
  clips.value = await StreamService.getVodClips(
    route.params.streamId,
    currentFile.value._id
  )
}

const updatePlaybackUrl = (url) => {
  playbackUrl.value = url
}

const isLineTabActive = (tab) => {
  switch (tab) {
    case 'embed':
      return  (activeTab.value === 'embed') || (activeTab.value === 'social')
    case 'social':
      return (activeTab.value === 'embed') || (activeTab.value === 'social')
    case 'chapters':
      return ((activeTab.value === 'chapters') || (activeTab.value === 'captions') || (activeTab.value === 'clips'))
    case 'captions':
      return (activeTab.value === 'captions') || (activeTab.value === 'chapters') || (activeTab.value === 'clips')
    case 'schedule':
      return (activeTab.value === 'schedule') ? true : false
    case 'analytics':
      return (activeTab.value === 'analytics') ? true : false
    case 'meta':
      return (activeTab.value === 'meta') ? true : false
    case 'clips':
      return (activeTab.value === 'clips')
    default:
      return true
  }
}

const shouldShowSubTab = (tab) => {
  switch (tab) {
    case 'embed':
      return  (activeTab.value === 'embed') || (activeTab.value === 'social') ? true : false
    case 'social':
      return (activeTab.value === 'embed') || (activeTab.value === 'social') ? true : false
    case 'chapters':
      return ((activeTab.value === 'chapters') || (activeTab.value === 'captions') || (activeTab.value === 'clips')) ? true : false
    case 'captions':
      return !isTrialOnly.value && !props.stream.liveParent && ((activeTab.value === 'captions') || (activeTab.value === 'chapters') || (activeTab.value === 'clips')) ? true : false
    case 'schedule':
      return (activeTab.value === 'schedule') ? true : false
    case 'analytics':
      return (activeTab.value === 'analytics') ? true : false
    case 'meta':
      return (activeTab.value === 'meta') ? true : false
    case 'clips':
      return !isLiveToVod.value && ((activeTab.value === 'captions') || (activeTab.value === 'chapters') || (activeTab.value === 'clips')) ? true : false
    default:
      return true
  }
}
const shouldHideSubTab = computed(() => {
  return ['schedule', 'analytics', 'meta'].includes(activeTab.value)
})

</script>
<template>
  <div>
    <Spinner v-if="showVideoSpinner" text="Retrieving data..." classes="text-dark-8 mh-60" spinner-color="var(--c-dark-8)"
      spinner-size="15px" />
    <div v-else-if="currentFile">
      <VODBreadcrumb class="mb-3" :items="breadcrumbItems" />
      <header class="flex flex-wrap items-center gap-3 mb-4 min-w-0">
        <div class="flex items-center gap-2 min-w-0 flex-1">
        <BackButton :to="backTo" />
        <Title :editable="true"
          titleClasses="text-xl font-semibold text-white truncate min-w-0 px-1.5 -mx-1.5 rounded-md hover:bg-surface-3 cursor-text min-h-8 flex items-center"
          edit-class="bg-surface-3 break-all" maxWidth="max-w-full"
          :title="displayFileName" @changed="onRenameTitle" />
        </div>
        <!-- Subtle tags inline to the right of the title: chips + a small "+ Tag" pill -->
        <VODTags v-if="currentFile" :key="currentFile?.id" class="vod-header-tags basis-full min-w-0 sm:basis-auto sm:shrink-0"
          :videoId="currentFile?.id" :tagIds="currentFile.tags || []"
          :liveParent="currentFile.archive" :streamParent="stream" />
      </header>

      <div class="grid gap-x-6 gap-y-6 xl:grid-cols-12 pb-24 items-start">
        <!-- LEFT: preview player + metadata form -->
        <div class="xl:col-span-7 flex flex-col gap-5">
          <div class="player-shell relative">
            <iframe :src="playerUrl" class="embed video-frame w-full h-full" frameborder="0"
              width="100%" scrolling="no" allow="autoplay; encrypted-media" allowfullscreen webkitallowfullscreen mozallowfullscreen oallowfullscreen
              msallowfullscreen />
            <div class="player-title-overlay" aria-hidden="true">
              <span class="player-title-text">{{ displayFileName }}</span>
            </div>
          </div>
          <VODVideoDetailsForm :vodFile="currentFile" :vodFolder="stream" :stream-meta="streamMeta"
            :video-meta="videoMeta" :playbackUrl="playbackUrl" @update="getData" />
        </div>

        <!-- RIGHT: tabs + settings — same card + pill-nav structure as the
             stream dashboard (StreamsManage): surface-2 card, surface-1 track -->
        <div class="xl:col-span-5 min-w-0 bg-surface-2 rounded-xl shadow-[0_2px_6px_rgba(0,0,0,0.28)] p-4 md:p-5">
          <div class="relative mb-4">
            <!-- Scroll-left affordance -->
            <FormButton v-show="tabCanScrollLeft" @click="scrollTabs(-1)" aria-label="Scroll tabs left"
              custom-classes="absolute left-0 top-1/2 -translate-y-1/2 z-20 size-7 grid place-items-center rounded-full bg-surface-3 ring-1 ring-surface-4 text-surface-8 hover:text-white hover:bg-surface-4 shadow-md transition-colors">
              <icon-base fill="none">
                <icon-chevron-left />
              </icon-base>
            </FormButton>
            <div v-show="tabCanScrollLeft"
              class="pointer-events-none absolute left-0 top-0 bottom-0 w-10 z-10 rounded-l-lg bg-gradient-to-r from-surface-1 to-transparent">
            </div>

            <div ref="tabBarRef" @scroll="updateTabScroll"
              @pointerdown="onTabPointerDown" @pointermove="onTabPointerMove"
              @pointerup="onTabPointerUp" @pointercancel="onTabPointerUp"
              @click.capture="onTabClickCapture" @dragstart.prevent
              class="inline-flex items-center gap-1 bg-surface-1 rounded-lg p-1 overflow-x-auto max-w-full no-scrollbar"
              :class="{ 'tab-draggable': tabCanScrollLeft || tabCanScrollRight }">
              <TabItem v-for="t in videoTabs" :key="t.key" type="custom" :disabled="t.disabled"
                :title="t.hint || ''" draggable="false"
                :custom-classes="'text-sm font-medium px-3 py-1.5 rounded-md flex items-center text-nowrap transition-all select-none disabled:opacity-50 disabled:cursor-not-allowed ' + (isTabActive(t.key) ? 'bg-surface-4 text-white shadow-sm' : 'text-surface-8 hover:text-white hover:bg-surface-3')"
                @click="!t.disabled && setTab(t.key)">
                {{ t.label }}
              </TabItem>
            </div>

            <!-- Scroll-right affordance -->
            <div v-show="tabCanScrollRight"
              class="pointer-events-none absolute right-0 top-0 bottom-0 w-10 z-10 rounded-r-lg bg-gradient-to-l from-surface-1 to-transparent">
            </div>
            <FormButton v-show="tabCanScrollRight" @click="scrollTabs(1)" aria-label="Scroll tabs right"
              custom-classes="absolute right-0 top-1/2 -translate-y-1/2 z-20 size-7 grid place-items-center rounded-full bg-surface-3 ring-1 ring-surface-4 text-surface-8 hover:text-white hover:bg-surface-4 shadow-md transition-colors">
              <icon-base fill="none" class="rotate-180">
                <icon-chevron-left />
              </icon-base>
            </FormButton>
          </div>
          <div>
            <VODEmbedTab v-if="isTabActive('embed') || isTabActive('player')" :sub-tab="activeTab"
              :vodFile="currentFile" :vodFolder="stream" :player-url="getPlayerUrl()" :stream-meta="streamMeta"
              @update:playbackUrl="updatePlaybackUrl" @update:meta="getData" />
            <div v-if="isTabActive('chapters')" class="rounded-lg ring-1 ring-surface-3 bg-surface-2 p-4">
              <VODChaptersTab :vodFile="currentFile" :vodFolder="stream" />
            </div>
            <VODCaptionsTab v-if="isTabActive('captions')" :vodFile="currentFile" :vodFolder="stream"
              :isLiveToVod="!!isLiveToVod" :player-url="getPlayerUrl()" />
            <div v-if="isTabActive('clips') && !isLiveToVod" class="rounded-lg ring-1 ring-surface-3 bg-surface-2 p-4">
              <VODClipsTab :vodFile="currentFile" :vodFolder="stream" :player-url="getPlayerUrl()" />
            </div>
            <div v-if="isTabActive('schedule')" class="rounded-lg ring-1 ring-surface-3 bg-surface-2 p-4">
              <VODScheduleTab :vodFile="currentFile" :vodFolder="stream"
                @video-metadata-schedule-updated="videoMetadataScheduleUpdated" :player-url="getPlayerUrl()"
                :video-meta="videoMeta" />
            </div>
            <div v-if="isTabActive('analytics')" class="flex flex-col gap-4">
              <VODAnalytics :currentFile="currentFile" />
              <div class="rounded-lg ring-1 ring-surface-3 bg-surface-2 p-4 flex items-center justify-between gap-3">
                <div class="min-w-0">
                  <h3 class="text-base font-medium text-white">Detailed analytics</h3>
                  <p class="text-surface-8 text-sm mt-0.5">Views, watch time and audience breakdowns for this video.</p>
                </div>
                <FormButton type="main" class="shrink-0" @click="onClickAnalytics">Open Analytics</FormButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <alert-modal modal-id="embed-upload-format"
      message="Only valid image files are allowed. Please pick an image file" />

    <alert-modal :message="'Uploaded image too large. Image must not exceed ' +
      maxPosterUploadMB +
      'MB'
      " modal-id="embed-image-size-exceed" />

    <alert-modal modal-id="embed-image-mime-type" message="Only PNG/JPG/GIF/AVIF image files are allowed" />

    <ModalBox v-model="isVisible" size="md" hide-close header-class="!hidden" wrapper-classes="lg:h-auto lg:max-h-none">

      <UppyUploaderReplace v-if="storageRestricitonsLoaded" :stream="stream" :max-files="1"
        :max-total-file-size="maxTotalFileSize" :maxFileSize="maxVideoFileSize ? maxVideoFileSize * 1000 ** 3 : maxVideoFileSize
          " :storageLimit="aioStorageLimit" :oldFileSize="currentFile.bytes" :replaceVideoId="currentFile.id"
        @upload-success="onFinishedUploadingVideoThroughCloud"
        :encodingPresets="canEncodeVideos ? availableEncodingPresets : []" />
    
</ModalBox>

    <upload-alert-modal modal-id="max-size-vod-storage-reached" message="Your storage limit has been exceeded" :body="maxFileSizeErrorMessage ||
      'This file is too large. Upload a smaller file or upgrade your plan to continue.'
      " ok-text="Cancel" button-alignment="text-right" />
  </div>
</template>

<style scoped>
.player-shell {
  aspect-ratio: 16/9;
  border-radius: 8px;
  overflow: hidden;
}

.player-shell .video-frame {
  display: block;
  height: 100%;
  border: 0;
}

/* Keep the tab bar horizontally scrollable on narrow screens, but hide the
   scrollbar (same helper as StreamsManage — it's scoped there, so redefine) */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Drag-to-scroll affordance for the tab bar (desktop mouse) */
.tab-draggable {
  cursor: grab;
}

.tab-draggable.tab-dragging {
  cursor: grabbing;
  user-select: none;
}

.player-title-overlay {
  position: absolute;
  inset: 0 0 auto 0;
  padding: 14px 16px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.65) 0%, rgba(0, 0, 0, 0) 100%);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.25s ease;
}

.player-shell:hover .player-title-overlay {
  opacity: 1;
}

/* Keep the header tags inline with the title, no extra vertical padding */
.vod-header-tags :deep(.flex.flex-wrap) {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  flex-wrap: nowrap;
}

.player-title-text {
  display: block;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
